// src/services/abrirPreventivasClienteApi.js
import {
  addDoc,
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "../config/firebase";
import {
  calcularCamadaExecutar,
  calcularProximaDataCamadas,
  montarAtualizacaoCamadas,
} from "../utils/preventivaCamadas";

const PERIODICIDADE_ORDEM = ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"];

function normalizarPeriodicidade(value) {
  const v = String(value || "").trim().toUpperCase();
  return PERIODICIDADE_ORDEM.includes(v) ? v : "";
}

function inferirPeriodicidadeItens(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return "";
  let maxIdx = -1;
  itens.forEach((item) => {
    const camada = normalizarPeriodicidade(item?.periodicidade);
    const idx = PERIODICIDADE_ORDEM.indexOf(camada);
    if (idx > maxIdx) maxIdx = idx;
  });
  return maxIdx >= 0 ? PERIODICIDADE_ORDEM[maxIdx] : "";
}

function montarNomePreventiva(baseNome, periodicidade) {
  const nome = String(baseNome || "").trim();
  const per = String(periodicidade || "").trim();
  if (!per) return nome;
  const upperNome = nome.toUpperCase();
  const upperPer = per.toUpperCase();
  if (upperNome.includes(upperPer)) return nome;
  return `${nome} ${per}`.trim();
}

function startOfDay(date) {
  const d = new Date(date || new Date());
  d.setHours(0, 0, 0, 0);
  return d;
}

function chunk(array, size) {
  const out = [];
  for (let i = 0; i < array.length; i += size) {
    out.push(array.slice(i, i + size));
  }
  return out;
}

function toDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function firstDayOfMonth(value) {
  const d = toDateSafe(value) || new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1, 0, 0, 0, 0);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function normalizeContratoTipoKey(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.includes("AVULSO")) return "AVULSO";
  if (raw.includes("QUIN")) return "QUINZENAL";
  if (raw.includes("MENS")) return "MENSAL";
  if (raw.includes("TRIM")) return "TRIMESTRAL";
  if (raw.includes("SEM")) return "SEMESTRAL";
  return raw;
}

function diffMonths(fromDate, toDate) {
  return (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
}

function deveGerarNoMes(contratoTipoKey, dataInicialMes, mesAlvo) {
  const dif = diffMonths(dataInicialMes, mesAlvo);
  if (dif < 0) return false;
  if (contratoTipoKey === "TRIMESTRAL") return dif % 3 === 0;
  if (contratoTipoKey === "SEMESTRAL") return dif % 6 === 0;
  return true;
}

function dataCompetencia(competencia) {
  const raw = String(competencia || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})(?:-(Q1|Q2))?$/i);
  if (!m) return new Date();
  const ano = Number(m[1]);
  const mes = Number(m[2]) - 1;
  const q = String(m[3] || "").toUpperCase();
  const dia = q === "Q2" ? 15 : 1;
  return new Date(ano, mes, dia, 12, 0, 0, 0);
}

function proximaQuinzenaData(referencia = new Date()) {
  const ref = startOfDay(referencia || new Date());
  const dia = ref.getDate();
  if (dia <= 14) {
    return new Date(ref.getFullYear(), ref.getMonth(), 15, 12, 0, 0, 0);
  }
  return new Date(ref.getFullYear(), ref.getMonth() + 1, 1, 12, 0, 0, 0);
}

function avancarQuinzena(dataAtual) {
  const d = startOfDay(dataAtual || new Date());
  if (d.getDate() <= 14) {
    return new Date(d.getFullYear(), d.getMonth(), 15, 12, 0, 0, 0);
  }
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 12, 0, 0, 0);
}

function camadaPlanejadaQuinzenalPorSlot(slotIndex) {
  const idx = Number(slotIndex || 1);
  if (!Number.isFinite(idx) || idx < 1) return "MENSAL";

  // Slots pares são sempre a quinzena operacional.
  if (idx % 2 === 0) return "QUINZENAL";

  // Slots ímpares são a camada de ciclo mensal/trimestral/semestral.
  const cicloIdx = Math.floor((idx + 1) / 2); // 1,2,3...
  const posSemestre = ((cicloIdx - 1) % 6) + 1; // 1..6
  if (posSemestre === 1) return "SEMESTRAL";
  if (posSemestre === 4) return "TRIMESTRAL";
  return "MENSAL";
}

function contratoAcademiaAtiva(contratos, setorId) {
  if (!Array.isArray(contratos) || contratos.length === 0) return false;
  const setor = String(setorId || "").trim();

  return contratos.some((contrato) => {
    const status = String(contrato?.status || "").trim().toUpperCase();
    if (status !== "APROVADO") return false;

    const academiasIds = Array.isArray(contrato?.academiasIds)
      ? contrato.academiasIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];

    if (academiasIds.length === 0) return true;
    if (!setor) return false;
    return academiasIds.includes(setor);
  });
}

function obterContratoAtivoAcademia(contratos, setorId) {
  if (!Array.isArray(contratos) || contratos.length === 0) return null;
  const setor = String(setorId || "").trim();

  for (const contrato of contratos) {
    const status = String(contrato?.status || "").trim().toUpperCase();
    if (status !== "APROVADO") continue;

    const academiasIds = Array.isArray(contrato?.academiasIds)
      ? contrato.academiasIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];

    if (academiasIds.length === 0) return contrato;
    if (setor && academiasIds.includes(setor)) return contrato;
  }

  return null;
}

async function getModelosByIds(ids) {
  const unique = Array.from(new Set((ids || []).map(String).filter(Boolean)));
  if (unique.length === 0) return new Map();

  const map = new Map();
  const parts = chunk(unique, 10);
  for (const part of parts) {
    const q = query(
      collection(db, "preventivasModelos"),
      where(documentId(), "in", part)
    );
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      map.set(d.id, { id: d.id, ...d.data() });
    });
  }
  return map;
}

async function commitInChunks(ops, maxOps = 450) {
  // Firestore batch hard limit = 500 ops; usa margem.
  const parts = chunk(ops, maxOps);
  for (const part of parts) {
    const batch = writeBatch(db);
    part.forEach((op) => op(batch));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}

export async function abrirPreventivasCliente({
  grupoId,
  clienteId,
  clienteNome,
  referencia = new Date(),
  criadoPor,
  usarReferenciaComoProximaData = false,
  contratoForcado = null,
  preencherProximaDataBase = true,
}) {
  if (!grupoId) throw new Error("grupoId nao informado");
  if (!clienteId) throw new Error("clienteId nao informado");

  const hoje = startOfDay(referencia);
  const diaSemana = hoje.getDay();
  const diaMes = hoje.getDate();

  const contratoForcadoTipo = String(
    contratoForcado?.contratoTipo || contratoForcado?.setorContratoTipo || ""
  ).trim();
  const contratoForcadoAtivo = !!contratoForcadoTipo
    && !contratoForcadoTipo.toLowerCase().includes("avulso");
  const academiasForcadasSet = Array.isArray(contratoForcado?.academiasIds)
    ? new Set(contratoForcado.academiasIds.map((id) => String(id || "").trim()).filter(Boolean))
    : null;

  const escopoPermiteSetor = (setorId) => {
    if (!contratoForcado) return true;
    if (!academiasForcadasSet || academiasForcadasSet.size === 0) return true;
    const setor = String(setorId || "").trim();
    return !!setor && academiasForcadasSet.has(setor);
  };

  // Contratos ativos aprovados do cliente (escopo por academia)
  let contratosCliente = [];
  try {
    const qContratos = query(
      collection(db, "financeiro_contratos"),
      where("grupoId", "==", grupoId),
      where("clienteId", "==", clienteId)
    );
    const snapContratos = await getDocs(qContratos);
    contratosCliente = snapContratos.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn("Falha ao carregar contratos para abertura de preventivas:", e);
    contratosCliente = [];
  }

  // 1) Carrega equipamentos do cliente
  const qMaquinas = query(
    collection(db, "maquinas"),
    where("grupoId", "==", grupoId),
    where("empresaId", "==", clienteId)
  );
  const snapMaquinas = await getDocs(qMaquinas);
  const maquinas = snapMaquinas.docs.map((d) => ({ id: d.id, ...d.data() }));

  // 2) Carrega preventivas existentes do cliente (para evitar duplicar e para preencher datas vazias)
  const qPrevCliente = query(
    collection(db, "preventivas"),
    where("grupoId", "==", grupoId),
    where("empresaId", "==", clienteId)
  );
  const snapPrevCliente = await getDocs(qPrevCliente);
  const preventivasCliente = snapPrevCliente.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Indexa por maquinaId -> set(modeloPreventivaId)
  const existentesPorMaquina = new Map();
  preventivasCliente
    .filter((p) => p?.ativo !== false)
    .forEach((p) => {
      const mid = String(p?.maquinaId || "");
      const modeloId = String(p?.modeloPreventivaId || "");
      if (!mid || !modeloId) return;
      if (!existentesPorMaquina.has(mid)) existentesPorMaquina.set(mid, new Set());
      existentesPorMaquina.get(mid).add(modeloId);
    });

  // 3) Descobre todos modelos necessários
  const todosModeloIds = [];
  maquinas.forEach((m) => {
    const ids = Array.isArray(m.preventivasModeloIds) ? m.preventivasModeloIds : [];
    ids.forEach((x) => todosModeloIds.push(String(x)));
  });
  const modelosMap = await getModelosByIds(todosModeloIds);

  const ops = [];
  let criadas = 0;
  let atualizadasDatas = 0;

  // 4) Preenche proximaData onde está ausente (primeira data)
  preventivasCliente.forEach((p) => {
    if (!p?.id) return;
    if (!preencherProximaDataBase) return;
    const temData = !!p?.proximaData;
    if (temData) return;
    const setorNoEscopo = escopoPermiteSetor(p?.setorId);
    const contratoAtivoRef = contratoForcado
      ? (setorNoEscopo ? contratoForcado : null)
      : obterContratoAtivoAcademia(contratosCliente, p?.setorId);
    const contratoTipo = contratoForcado
      ? (setorNoEscopo ? contratoForcadoTipo : "")
      : String(
        p?.contratoTipo || p?.setorContratoTipo || contratoAtivoRef?.contratoTipo || ""
      ).trim();
    const contratoAtivo = contratoForcado
      ? (setorNoEscopo && contratoForcadoAtivo)
      : (!!contratoAtivoRef && !!contratoTipo && !contratoTipo.toLowerCase().includes("avulso"));
    if (!contratoAtivo) return;
    ops.push((batch) => {
      const ref = doc(db, "preventivas", p.id);
      batch.update(ref, {
        proximaData: usarReferenciaComoProximaData
          ? hoje
          : calcularProximaDataCamadas({ ...p, contratoTipo }, hoje),
        diaSemana: p?.diaSemana ?? diaSemana,
        diaMes: p?.diaMes ?? diaMes,
        atualizadoEm: serverTimestamp(),
      });
    });
    atualizadasDatas += 1;
  });

  // 5) Cria instâncias faltantes por equipamento/modelo
  maquinas.forEach((maq) => {
    if (!escopoPermiteSetor(maq?.setorId)) return;

    const ids = Array.isArray(maq.preventivasModeloIds) ? maq.preventivasModeloIds : [];
    const existentesSet = existentesPorMaquina.get(String(maq.id)) || new Set();

    ids
      .map(String)
      .filter(Boolean)
      .filter((modeloId) => !existentesSet.has(String(modeloId)))
      .forEach((modeloId) => {
        const modelo = modelosMap.get(String(modeloId));
        if (!modelo) return;

        const contratoAtivoRef = contratoForcado
          ? contratoForcado
          : obterContratoAtivoAcademia(contratosCliente, maq?.setorId);
        const contratoTipo = contratoForcado
          ? contratoForcadoTipo
          : String(maq?.setorContratoTipo || contratoAtivoRef?.contratoTipo || "").trim();
        const contratoAtivo = contratoForcado
          ? contratoForcadoAtivo
          : (!!contratoAtivoRef && !!contratoTipo && !contratoTipo.toLowerCase().includes("avulso"));
        const proximaData = contratoAtivo
          ? (preencherProximaDataBase
            ? (usarReferenciaComoProximaData ? hoje : calcularProximaDataCamadas({ contratoTipo }, hoje))
            : null)
          : null;

        const periodicidadeModelo =
          modelo.periodicidade || inferirPeriodicidadeItens(modelo.itens);
        ops.push((batch) => {
          const prevRef = doc(collection(db, "preventivas"));
          batch.set(prevRef, {
            grupoId,

            empresaId: clienteId,
            empresaNome: clienteNome || maq.empresaNome || "",

            maquinaId: maq.id,
            maquinaNome: maq.nome || maq.maquinaNome || "",

            setorId: maq.setorId || null,
            setorNome: maq.setorNome || "",
            setorCodigo: maq.setorCodigo || "",

            modeloPreventivaId: modelo.id,
            modeloPreventivaCodigo: modelo.codigo || "",

            codigo: modelo.codigo || "",
            nome: montarNomePreventiva(modelo.nome || "", periodicidadeModelo),
            fabricante: modelo.fabricante || "",
            modelo: modelo.modelo || "",
            periodicidade: periodicidadeModelo || "",
            diaSemana: modelo.diaSemana ?? diaSemana,
            diaMes: modelo.diaMes ?? diaMes,
            itens: Array.isArray(modelo.itens) ? modelo.itens : [],

            contratoTipo,
            proximaData,
            ativo: contratoAtivo,

            criadoEm: serverTimestamp(),
            criadoPor: criadoPor || "",
            atualizadoEm: serverTimestamp(),
          });
        });

        criadas += 1;
      });
  });

  await commitInChunks(ops);

  return {
    maquinas: maquinas.length,
    preventivasExistentes: preventivasCliente.length,
    criadas,
    datasPreenchidas: atualizadasDatas,
  };
}

export async function sincronizarPlanejamentoContratoAprovado({
  grupoId,
  clienteId,
  clienteNome,
  empresaId,
  contrato,
  criadoPor,
  aprovadoEm,
  mesesAntecedencia = 12,
}) {
  if (!grupoId) throw new Error("grupoId nao informado");
  if (!clienteId) throw new Error("clienteId nao informado");

  const contratoTipoKey = normalizeContratoTipoKey(
    contrato?.contratoTipo || contrato?.setorContratoTipo || ""
  );
  if (!contratoTipoKey || contratoTipoKey === "AVULSO") {
    return {
      atendimentosCriados: 0,
      sessoesCriadas: 0,
      itensCriados: 0,
      preventivasAbertas: 0,
      preventivasDatasPreenchidas: 0,
    };
  }

  const statusContrato = String(contrato?.status || "").trim().toUpperCase();
  if (statusContrato !== "APROVADO") {
    return {
      atendimentosCriados: 0,
      sessoesCriadas: 0,
      itensCriados: 0,
      preventivasAbertas: 0,
      preventivasDatasPreenchidas: 0,
    };
  }

  const empresaIdValue = String(empresaId || contrato?.empresaId || clienteId).trim();
  const referenciaAprovacao =
    toDateSafe(aprovadoEm)
    || toDateSafe(contrato?.aprovadoEm)
    || toDateSafe(contrato?.updatedAt)
    || new Date();
  const referenciaInicial = toDateSafe(contrato?.dataInicial) || referenciaAprovacao;
  const primeiraCompetenciaData = proximaQuinzenaData(referenciaInicial);
  const hojeMes = firstDayOfMonth(new Date());
  const inicioCompetenciaMes = firstDayOfMonth(primeiraCompetenciaData);
  const inicioPlanejamento = inicioCompetenciaMes > hojeMes ? inicioCompetenciaMes : hojeMes;
  const duracaoMeses = Number(contrato?.duracaoMeses);
  const totalMeses = Number.isFinite(duracaoMeses) && duracaoMeses > 0
    ? duracaoMeses
    : (Number(mesesAntecedencia) > 0 ? Number(mesesAntecedencia) : 12);

  const resPreventivas = await abrirPreventivasCliente({
    grupoId,
    clienteId,
    clienteNome,
    referencia: inicioPlanejamento,
    criadoPor,
    contratoForcado: contrato,
    preencherProximaDataBase: false,
  });

  const academiasContrato = Array.isArray(contrato?.academiasIds)
    ? contrato.academiasIds.map((id) => String(id || "").trim()).filter(Boolean)
    : [];

  const snapPrev = await getDocs(
    query(
      collection(db, "preventivas"),
      where("grupoId", "==", grupoId),
      where("empresaId", "==", empresaIdValue)
    )
  );

  let preventivasClienteEscopo = snapPrev.docs
    .map((d) => ({ id: d.id, ...d.data() }));

  if (academiasContrato.length > 0) {
    const setAcademias = new Set(academiasContrato);
    preventivasClienteEscopo = preventivasClienteEscopo.filter((p) => {
      const setorId = String(p?.setorId || "").trim();
      return !!setorId && setAcademias.has(setorId);
    });
  }

  if (!preventivasClienteEscopo.length) {
    return {
      atendimentosCriados: 0,
      sessoesCriadas: 0,
      itensCriados: 0,
      preventivasAbertas: Number(resPreventivas?.criadas || 0),
      preventivasDatasPreenchidas: Number(resPreventivas?.datasPreenchidas || 0),
      preventivasPlanejadasCriadas: 0,
    };
  }

  const preventivasBase = preventivasClienteEscopo.filter((p) => p?.planejadaPorContrato !== true);
  const preventivasPlanejadasExistentesTodos = preventivasClienteEscopo.filter(
    (p) => p?.planejadaPorContrato === true
  );

  if (!preventivasBase.length) {
    return {
      atendimentosCriados: 0,
      sessoesCriadas: 0,
      itensCriados: 0,
      preventivasAbertas: Number(resPreventivas?.criadas || 0),
      preventivasDatasPreenchidas: Number(resPreventivas?.datasPreenchidas || 0),
      preventivasPlanejadasCriadas: 0,
    };
  }

  const competencias = [];
  if (contratoTipoKey === "QUINZENAL") {
    const totalSlots = Math.max(totalMeses * 2, 1);
    let dataSlot = proximaQuinzenaData(referenciaInicial);
    for (let slotIndex = 1; slotIndex <= totalSlots; slotIndex += 1) {
      const mesValue = monthKey(dataSlot);
      const quinzenaValue = dataSlot.getDate() <= 14 ? "Q1" : "Q2";
      competencias.push({
        competencia: `${mesValue}-${quinzenaValue}`,
        competenciaMes: mesValue,
        quinzena: quinzenaValue,
        slotIndex,
        dataSlot,
      });
      dataSlot = avancarQuinzena(dataSlot);
    }
  } else {
    for (let i = 0; i < totalMeses; i += 1) {
      const mes = addMonths(inicioPlanejamento, i);
      if (!deveGerarNoMes(contratoTipoKey, inicioCompetenciaMes, mes)) continue;
      const mesValue = monthKey(mes);
      competencias.push({ competencia: mesValue, competenciaMes: mesValue, quinzena: null });
    }
  }

  if (!competencias.length) {
    return {
      atendimentosCriados: 0,
      sessoesCriadas: 0,
      itensCriados: 0,
      preventivasAbertas: Number(resPreventivas?.criadas || 0),
      preventivasDatasPreenchidas: Number(resPreventivas?.datasPreenchidas || 0),
      preventivasPlanejadasCriadas: 0,
    };
  }

  const targetSlotKeys = new Set();
  for (const comp of competencias) {
    for (const prev of preventivasBase) {
      targetSlotKeys.add(`${prev.id}|${comp.competencia}`);
    }
  }

  const hojeRef = startOfDay(new Date());
  const opsCleanupPlanejadas = [];
  const preventivasPlanejadasBySlot = new Map();
  const preventivasPlanejadasExistentes = [];
  preventivasPlanejadasExistentesTodos
    .forEach((p) => {
      const slot = String(p?.slotKey || "").trim();
      preventivasPlanejadasExistentes.push(p);
      if (!slot) return;

      // Se já existe planejada para o mesmo slotKey, inativa duplicata legada.
      if (preventivasPlanejadasBySlot.has(slot)) {
        opsCleanupPlanejadas.push((batch) => {
          batch.update(doc(db, "preventivas", p.id), {
            ativo: false,
            atualizadoEm: serverTimestamp(),
          });
        });
        return;
      }

      preventivasPlanejadasBySlot.set(slot, p);
    });
  preventivasPlanejadasExistentes.forEach((p) => {
    const slot = String(p?.slotKey || "").trim();
    const mesmaEmpresa = String(p?.empresaId || "").trim() === String(empresaIdValue || "").trim();
    const mesmoContrato = !contrato?.id || String(p?.contratoId || "").trim() === String(contrato?.id || "").trim();
    if (!slot || !mesmaEmpresa || !mesmoContrato) return;
    if (targetSlotKeys.has(slot)) return;
    opsCleanupPlanejadas.push((batch) => {
      batch.update(doc(db, "preventivas", p.id), {
        ativo: false,
        atualizadoEm: serverTimestamp(),
      });
    });
  });

  const opsPlanejadas = [];
  const opsLimparBase = [];
  const competenciasOrdenadas = [...competencias].sort(
    (a, b) => dataCompetencia(a.competencia).getTime() - dataCompetencia(b.competencia).getTime()
  );

  let preventivasPlanejadasCriadas = 0;

  for (const prev of preventivasBase) {
    if (prev?.ativo !== false || prev?.proximaData) {
      opsLimparBase.push((batch) => {
        batch.update(doc(db, "preventivas", prev.id), {
          ativo: false,
          proximaData: null,
          atualizadoEm: serverTimestamp(),
        });
      });
    }

    let estadoSimulado = {
      ...prev,
      contratoTipo: contrato?.contratoTipo || prev?.contratoTipo || "Mensal",
    };

    for (const comp of competenciasOrdenadas) {
      const setorId = String(prev?.setorId || "").trim();
      if (!setorId) continue;
      const slotKey = `${prev.id}|${comp.competencia}`;

      const dataExec = dataCompetencia(comp.competencia);
      const deveEstarAtiva = startOfDay(dataExec).getTime() <= hojeRef.getTime();
      const camadaDeterministicaQuinzenal = contratoTipoKey === "QUINZENAL"
        ? camadaPlanejadaQuinzenalPorSlot(comp?.slotIndex)
        : "";
      const camadaPrevista = camadaDeterministicaQuinzenal
        || calcularCamadaExecutar(estadoSimulado, dataExec)
        || normalizarPeriodicidade(prev?.periodicidade)
        || "MENSAL";

      const existente = preventivasPlanejadasBySlot.get(slotKey) || null;
      if (existente?.id) {
        const camadaAtual = String(existente?.camadaPrevista || "").toUpperCase();
        const periodicidadeAtual = String(existente?.periodicidade || "").toUpperCase();
        const ativoAtual = existente?.ativo !== false;
        if (camadaAtual !== camadaPrevista || periodicidadeAtual !== camadaPrevista || ativoAtual !== deveEstarAtiva) {
          opsPlanejadas.push((batch) => {
            batch.update(doc(db, "preventivas", existente.id), {
              camadaPrevista,
              periodicidade: camadaPrevista,
              ativo: deveEstarAtiva,
              atualizadoEm: serverTimestamp(),
            });
          });
        }

        estadoSimulado = {
          ...estadoSimulado,
          ...montarAtualizacaoCamadas(camadaPrevista, dataExec, estadoSimulado?.contratoTipo),
          ultimaExecucao: dataExec,
          ultimaExecucaoCiclo: dataExec,
        };
        continue;
      }

      const refPrev = doc(collection(db, "preventivas"));
      const payloadPrev = {
        grupoId,
        empresaId: empresaIdValue,
        empresaNome: prev?.empresaNome || clienteNome || "",
        maquinaId: prev?.maquinaId || null,
        maquinaNome: prev?.maquinaNome || "",
        setorId,
        setorNome: prev?.setorNome || "",
        setorCodigo: prev?.setorCodigo || "",
        modeloPreventivaId: prev?.modeloPreventivaId || null,
        modeloPreventivaCodigo: prev?.modeloPreventivaCodigo || prev?.codigo || "",
        codigo: prev?.codigo || prev?.modeloPreventivaCodigo || "",
        nome: prev?.nome || "Preventiva",
        fabricante: prev?.fabricante || "",
        modelo: prev?.modelo || "",
        periodicidade: camadaPrevista,
        camadaPrevista,
        diaSemana: prev?.diaSemana ?? null,
        diaMes: prev?.diaMes ?? null,
        itens: Array.isArray(prev?.itens) ? prev.itens : [],
        contratoTipo: contrato?.contratoTipo || prev?.contratoTipo || "Mensal",
        contratoId: contrato?.id || null,
        contratoStatus: "APROVADO",
        ativo: deveEstarAtiva,
        proximaData: dataCompetencia(comp.competencia),
        planejadaPorContrato: true,
        origemPreventivaId: prev.id,
        competencia: comp.competencia,
        competenciaMes: comp.competenciaMes,
        quinzena: comp.quinzena,
        slotKey,
        criadoEm: serverTimestamp(),
        criadoPor: criadoPor || "",
        atualizadoEm: serverTimestamp(),
      };

      opsPlanejadas.push((batch) => batch.set(refPrev, payloadPrev));
      preventivasPlanejadasBySlot.set(slotKey, { id: refPrev.id, ...payloadPrev });
      preventivasPlanejadasCriadas += 1;

      estadoSimulado = {
        ...estadoSimulado,
        ...montarAtualizacaoCamadas(camadaPrevista, dataExec, estadoSimulado?.contratoTipo),
        ultimaExecucao: dataExec,
        ultimaExecucaoCiclo: dataExec,
      };
    }
  }

  if (opsPlanejadas.length) {
    await commitInChunks(opsPlanejadas);
  }

  if (opsLimparBase.length) {
    await commitInChunks(opsLimparBase);
  }

  if (opsCleanupPlanejadas.length) {
    await commitInChunks(opsCleanupPlanejadas);
  }

  const snapAtendimentos = await getDocs(
    query(
      collection(db, "atendimentos_preventiva"),
      where("grupoId", "==", grupoId),
      where("empresaId", "==", empresaIdValue),
      where("clienteId", "==", clienteId)
    )
  );

  const atendimentoByKey = new Map();
  snapAtendimentos.docs.forEach((d) => {
    const data = d.data() || {};
    const competencia = String(data?.competencia || "").trim();
    const setorId = String(data?.setorId || data?.academiaId || "").trim();
    if (!competencia || !setorId) return;
    atendimentoByKey.set(`${competencia}|${setorId}`, { id: d.id, ...data });
  });

  let atendimentosCriados = 0;
  for (const comp of competencias) {
    for (const prev of preventivasBase) {
      const setorId = String(prev?.setorId || "").trim();
      if (!setorId) continue;
      const key = `${comp.competencia}|${setorId}`;
      if (atendimentoByKey.has(key)) continue;

      const refAt = await addDoc(collection(db, "atendimentos_preventiva"), {
        grupoId,
        empresaId: empresaIdValue,
        clienteId,
        contratoTipo: contrato?.contratoTipo || "Mensal",
        competencia: comp.competencia,
        status: "ABERTO",
        startedAt: null,
        progresso: null,
        setorId,
        academiaId: setorId,
        contratoId: contrato?.id || null,
        contratoStatus: "APROVADO",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      atendimentoByKey.set(key, {
        id: refAt.id,
        grupoId,
        empresaId: empresaIdValue,
        clienteId,
        contratoTipo: contrato?.contratoTipo || "Mensal",
        competencia: comp.competencia,
        setorId,
        academiaId: setorId,
      });
      atendimentosCriados += 1;
    }
  }

  const sessaoByAtendimentoId = new Map();
  let sessoesCriadas = 0;

  for (const atendimento of atendimentoByKey.values()) {
    const atendimentoId = String(atendimento?.id || "").trim();
    if (!atendimentoId) continue;
    const snapSess = await getDocs(
      query(
        collection(db, "atendimentos_sessoes"),
        where("grupoId", "==", grupoId),
        where("empresaId", "==", empresaIdValue),
        where("atendimentoId", "==", atendimentoId),
        limit(1)
      )
    );

    if (!snapSess.empty) {
      const d = snapSess.docs[0];
      sessaoByAtendimentoId.set(atendimentoId, { id: d.id, ...d.data() });
      continue;
    }

    const refSess = await addDoc(collection(db, "atendimentos_sessoes"), {
      grupoId,
      empresaId: empresaIdValue,
      atendimentoId,
      tecnicoId: null,
      inicioEm: null,
      fimEm: null,
      status: "PLANEJADA",
      observacoes: null,
      propostaStatus: "APROVADA",
      liberadaAgendamento: true,
      contratoId: contrato?.id || null,
      contratoStatus: "APROVADO",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    sessaoByAtendimentoId.set(atendimentoId, {
      id: refSess.id,
      grupoId,
      empresaId: empresaIdValue,
      atendimentoId,
    });
    sessoesCriadas += 1;
  }

  const slotKeysBySessao = new Map();
  for (const sessao of sessaoByAtendimentoId.values()) {
    const sid = String(sessao?.id || "").trim();
    if (!sid) continue;
    const snapItens = await getDocs(
      query(
        collection(db, "atendimentos_sessao_itens"),
        where("grupoId", "==", grupoId),
        where("empresaId", "==", empresaIdValue),
        where("sessaoId", "==", sid),
        where("tipo", "==", "PREVENTIVA")
      )
    );
    const keys = new Set();
    snapItens.docs.forEach((d) => {
      const it = d.data() || {};
      const slot = String(it?.slotKey || "").trim();
      if (slot) keys.add(slot);
    });
    slotKeysBySessao.set(sid, keys);
  }

  const itemOps = [];
  let itensCriados = 0;

  for (const comp of competencias) {
    for (const prev of preventivasBase) {
      const setorId = String(prev?.setorId || "").trim();
      if (!setorId) continue;

      const atKey = `${comp.competencia}|${setorId}`;
      const atendimento = atendimentoByKey.get(atKey);
      if (!atendimento?.id) continue;

      const sessao = sessaoByAtendimentoId.get(String(atendimento.id));
      if (!sessao?.id) continue;

      const slotKey = `${prev.id}|${comp.competencia}`;
      const preventivaPlanejada = preventivasPlanejadasBySlot.get(slotKey) || prev;
      const existingSlots = slotKeysBySessao.get(sessao.id) || new Set();
      if (existingSlots.has(slotKey)) continue;

      itemOps.push((batch) => {
        const ref = doc(collection(db, "atendimentos_sessao_itens"));
        batch.set(ref, {
          grupoId,
          empresaId: empresaIdValue,
          atendimentoId: atendimento.id,
          sessaoId: sessao.id,
          clienteId,
          tipo: "PREVENTIVA",
          equipamentoId: null,
          equipamentoNome: null,
          preventivaModeloId: preventivaPlanejada?.modeloPreventivaId || prev?.modeloPreventivaId || null,
          checklistInstalacaoId: null,
          checklistInstalacaoNome: null,
          propostaInstalacaoId: null,
          propostaInstalacaoNome: null,
          preventivaId: preventivaPlanejada?.id || prev.id,
          preventivaCodigo: preventivaPlanejada?.codigo || preventivaPlanejada?.modeloPreventivaCodigo || prev?.codigo || prev?.modeloPreventivaCodigo || null,
          preventivaNome: preventivaPlanejada?.nome || prev?.nome || null,
          maquinaId: preventivaPlanejada?.maquinaId || prev?.maquinaId || null,
          maquinaNome: preventivaPlanejada?.maquinaNome || prev?.maquinaNome || null,
          periodicidade: preventivaPlanejada?.periodicidade || prev?.periodicidade || null,
          camadaPrevista: preventivaPlanejada?.camadaPrevista || prev?.camadaPrevista || null,
          osId: null,
          osCodigo: null,
          osTitulo: null,
          osTipoServico: null,
          setorId,
          setorNome: prev?.setorNome || null,
          competencia: comp.competencia,
          competenciaMes: comp.competenciaMes,
          quinzena: comp.quinzena,
          slotKey,
          createdAt: serverTimestamp(),
        });
      });

      existingSlots.add(slotKey);
      slotKeysBySessao.set(sessao.id, existingSlots);
      itensCriados += 1;
    }
  }

  if (itemOps.length) {
    await commitInChunks(itemOps);
  }

  return {
    atendimentosCriados,
    sessoesCriadas,
    itensCriados,
    preventivasAbertas: Number(resPreventivas?.criadas || 0),
    preventivasDatasPreenchidas: Number(resPreventivas?.datasPreenchidas || 0),
    preventivasPlanejadasCriadas,
  };
}

export async function ativarPreventivasPlanejadasAteHoje({
  grupoId,
  empresaId,
  referencia = new Date(),
}) {
  const gid = String(grupoId || "").trim();
  const eid = String(empresaId || "").trim();
  if (!gid) return { ativadas: 0 };

  const hojeRef = startOfDay(toDateSafe(referencia) || new Date());

  let snap;
  if (eid) {
    try {
      snap = await getDocs(
        query(
          collection(db, "preventivas"),
          where("grupoId", "==", gid),
          where("empresaId", "==", eid),
          where("planejadaPorContrato", "==", true)
        )
      );
    } catch {
      snap = await getDocs(
        query(
          collection(db, "preventivas"),
          where("grupoId", "==", gid),
          where("empresaId", "==", eid)
        )
      );
    }
  } else {
    try {
      snap = await getDocs(
        query(
          collection(db, "preventivas"),
          where("grupoId", "==", gid),
          where("planejadaPorContrato", "==", true)
        )
      );
    } catch {
      snap = await getDocs(
        query(
          collection(db, "preventivas"),
          where("grupoId", "==", gid)
        )
      );
    }
  }

  const ops = [];
  let ativadas = 0;
  snap.docs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (data?.planejadaPorContrato !== true) return;

    const dataComp = toDateSafe(data?.proximaData);
    if (!dataComp) return;

    const deveAtivar = startOfDay(dataComp).getTime() <= hojeRef.getTime();
    if (!deveAtivar) return;
    if (data?.ativo !== false) return;

    ops.push((batch) => {
      batch.update(doc(db, "preventivas", docSnap.id), {
        ativo: true,
        atualizadoEm: serverTimestamp(),
      });
    });
    ativadas += 1;
  });

  if (ops.length) {
    await commitInChunks(ops);
  }

  return { ativadas };
}
